import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Polyline, Marker as LeafletMarker } from 'react-leaflet';
import L, { LatLngBounds, Icon } from 'leaflet';
import HereMap from './HereMap';
import { itemSVG } from '../../lib/map_helpers';
import Area from './layers/Area';

const generateDarkColor = (index) => {
  const baseHue = 180;
  const hueStep = 40;
  return `hsl(${(baseHue + index * hueStep) % 360}, 50%, 30%)`;
};

const calculateBearing = (p1Lat, p1Lng, p2Lat, p2Lng) => {
  const toRadians = (deg) => (Number(deg) * Math.PI) / 180;
  const lat1 = toRadians(p1Lat);
  const lng1 = toRadians(p1Lng);
  const lat2 = toRadians(p2Lat);
  const lng2 = toRadians(p2Lng);

  const y = Math.sin(lng2 - lng1) * Math.cos(lat2);
  const x =
      Math.cos(lat1) * Math.sin(lat2) -
      Math.sin(lat1) * Math.cos(lat2) * Math.cos(lng2 - lng1);

  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
};

class RoutingTestsMap extends Component {
  constructor(props) {
    super(props);
    this.state = {
      waypoints: props.routingTest.waypoints,
      route: props.routingTest.route,
      boundingBox: props.boundingBox
    };
  }

  componentDidMount() {
    const { waypoints } = this.state;
    if (waypoints?.length > 1) {
      this.setState({ boundingBox: new LatLngBounds(waypoints) });
    }
  }

  renderWaypointMarkers = () => {
    const { waypoints } = this.state;
    if (!waypoints) return null;

    return waypoints.map((waypoint, index) => {
      if (!waypoint?.length) return null;

      const color = generateDarkColor(index);
      const icon = new Icon({
        iconUrl: itemSVG(color, index + 1),
        iconSize: [40, 40],
        iconAnchor: [20, 20]
      });

      const key = `waypoint-marker-${waypoint[0]}-${waypoint[1]}`;

      return (
          <LeafletMarker
              key={key}
              position={waypoint}
              icon={icon}
              zIndexOffset={800}
              type="waypoint"
          />
      );
    });
  };

  renderRoute = () => {
    const { route } = this.state;
    if (!route?.length) return null;

    return (
        <Polyline
            color="#222222"
            positions={route}
            weight={5}
            pane="overlayPane"
            opacity={0.3}
        />
    );
  };

  renderRestrictedAreas = () => {
    const { restrictedAreas } = this.props;
    if (!restrictedAreas) return null;

    return restrictedAreas.map((area) => {
      if (!area?.id || !area.bounding_box) return null;

      return (
          <Area
              key={area.id}
              area={area.area_type === 'bbox' ? area.bounding_box : area.area}
              color="#ff2b79"
              opacity={0.8}
              fillColor="#ff2b79"
          />
      );
    });
  };

  getViaApproachRedArrowP1Positions = () => {
    const { route, waypoints } = this.state;
    const redArrowP1Positions = new Set();
    if (!route?.length || !waypoints || waypoints.length <= 2) return redArrowP1Positions;

    const epsilon = 0.00001;
    waypoints.slice(1, -1).forEach((viaWaypoint, i) => {
      const prevMajorWaypoint = waypoints[i];
      if (!viaWaypoint?.length || !prevMajorWaypoint?.length) return;

      const currentViaWaypointIndexInRoute = route.findIndex(
          (p) =>
              p?.length >= 2 &&
              Math.abs(p[0] - viaWaypoint[0]) < epsilon &&
              Math.abs(p[1] - viaWaypoint[1]) < epsilon
      );

      const prevMajorWaypointIndexInRoute = route.findIndex(
          (p) =>
              p?.length >= 2 &&
              Math.abs(p[0] - prevMajorWaypoint[0]) < epsilon &&
              Math.abs(p[1] - prevMajorWaypoint[1]) < epsilon
      );

      if (
          currentViaWaypointIndexInRoute > 0 &&
          prevMajorWaypointIndexInRoute !== -1 &&
          currentViaWaypointIndexInRoute > prevMajorWaypointIndexInRoute + 2
      ) {
        const p1ForRedArrow = route[currentViaWaypointIndexInRoute - 1];
        if (p1ForRedArrow?.length) {
          redArrowP1Positions.add(`${p1ForRedArrow[0]},${p1ForRedArrow[1]}`);
        }
      }
    });

    return redArrowP1Positions;
  };

  renderApproachArrows = () => {
    const { route, waypoints } = this.state;
    if (!route?.length || !waypoints || waypoints.length <= 2) return null;

    const epsilon = 0.00001;
    return waypoints.slice(1, -1).map((viaWaypoint, i) => {
      if (!viaWaypoint?.length) return null;

      const currentViaWaypointIndexInRoute = route.findIndex(
          (p) =>
              p?.length >= 2 &&
              Math.abs(p[0] - viaWaypoint[0]) < epsilon &&
              Math.abs(p[1] - viaWaypoint[1]) < epsilon
      );

      const prevMajorWaypoint = waypoints[i];
      if (!prevMajorWaypoint?.length) return null;

      const prevMajorWaypointIndexInRoute = route.findIndex(
          (p) =>
              p?.length >= 2 &&
              Math.abs(p[0] - prevMajorWaypoint[0]) < epsilon &&
              Math.abs(p[1] - prevMajorWaypoint[1]) < epsilon
      );

      if (
          currentViaWaypointIndexInRoute > 0 &&
          prevMajorWaypointIndexInRoute !== -1 &&
          currentViaWaypointIndexInRoute > prevMajorWaypointIndexInRoute + 2
      ) {
        const [p1, p2] = [
          route[currentViaWaypointIndexInRoute - 1],
          route[currentViaWaypointIndexInRoute]
        ];
        if (!p1?.length || !p2?.length) return null;

        const bearing = calculateBearing(p1[0], p1[1], p2[0], p2[1]);
        const arrowSvg = `
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20">
            <polygon points="10,0 16,14 4,14" style="fill:red;" />
          </svg>
        `;

        const arrowIcon = new L.DivIcon({
          html: `
            <div style="
              transform: rotate(${bearing}deg); 
              transform-origin: center center; 
              width: 20px; 
              height: 20px; 
              display: flex; 
              align-items: center; 
              justify-content: center;">
              ${arrowSvg}
            </div>
          `,
          className: 'leaflet-approach-arrow-icon leaflet-marker-icon',
          iconSize: [20, 20],
          iconAnchor: [10, 10]
        });

        return (
            <LeafletMarker
                key={`approach-arrow-${i}`}
                position={p1}
                icon={arrowIcon}
                zIndexOffset={790}
            />
        );
      }
      return null;
    });
  };

  renderGeneralDirectionArrows = () => {
    const { route } = this.state;
    if (!route || route.length < 2) return null;

    const redArrowP1Positions = this.getViaApproachRedArrowP1Positions();
    const arrowColor = '#444444';
    const arrowSize = 12;
    const generalArrowInterval = 10;

    return route.slice(0, -1).map((p1, j) => {
      const p2 = route[j + 1];
      if (!p1?.length || !p2?.length) return null;

      const p1Str = `${p1[0]},${p1[1]}`;
      if (!redArrowP1Positions.has(p1Str) && j % generalArrowInterval === 0 && j > 0) {
        const bearing = calculateBearing(p1[0], p1[1], p2[0], p2[1]);

        const generalArrowSvg = `
          <svg xmlns="http://www.w3.org/2000/svg" width="${arrowSize}" 
            height="${arrowSize}" viewBox="0 0 ${arrowSize} ${arrowSize}">
            <polyline points="${arrowSize * 0.25},${arrowSize * 0.65} ${arrowSize * 0.5},
              ${arrowSize * 0.35} ${arrowSize * 0.75},${arrowSize * 0.65}" 
              style="fill:none; stroke:${arrowColor}; stroke-width:1.5;
              stroke-linecap:round; stroke-linejoin:round;" />
          </svg>
        `;

        const generalArrowIcon = new L.DivIcon({
          html: `<div style="
            transform: rotate(${bearing}deg); 
            transform-origin: center center; 
            width: ${arrowSize}px; 
            height: ${arrowSize}px; 
            display: flex; 
            align-items: center; 
            justify-content: center;
            opacity: 1;">
            ${generalArrowSvg}
          </div>`,
          className: 'leaflet-general-direction-arrow-icon leaflet-marker-icon',
          iconSize: [arrowSize, arrowSize],
          iconAnchor: [arrowSize / 2, arrowSize / 2]
        });

        return (
            <LeafletMarker
                key={`general-arrow-${j}`}
                position={p1}
                icon={generalArrowIcon}
                zIndexOffset={780}
            />
        );
      }
      return null;
    });
  };

  render() {
    const { boundingBox } = this.state;

    return (
        <HereMap boundingBox={boundingBox} autoZoom noTraffic>
          {this.renderRestrictedAreas()}
          {this.renderRoute()}
          {this.renderGeneralDirectionArrows()}
          {this.renderApproachArrows()}
          {this.renderWaypointMarkers()}
        </HereMap>
    );
  }
}

RoutingTestsMap.propTypes = {
  boundingBox: PropTypes.object.isRequired,
  routingTest: PropTypes.object.isRequired,
  restrictedAreas: PropTypes.array
};

export default RoutingTestsMap;
